import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy } from './firestore-compat';

// ── Helpers ────────────────────────────────────────────────────────────────────
function toSeconds(val) {
  if (!val || val === '') return 0;
  const s = String(val).trim();
  // Formato Openzap: "0D 00:00:00"
  const match = s.match(/(\d+)D\s+(\d+):(\d+):(\d+)/);
  if (match) {
    const days = parseInt(match[1]);
    const hours = parseInt(match[2]);
    const minutes = parseInt(match[3]);
    const seconds = parseInt(match[4]);
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }
  return 0;
}

function secsToHMS(s) {
  if (!s || s === 0) return '0s';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2,'0')}s`;
  return `${sec}s`;
}

function parseDateTime(s) {
  if (!s) return null;
  const str = String(s).trim();
  // Formato Openzap: 18-05-2026 06:29:42 (DD-MM-YYYY HH:mm:ss)
  const m1 = str.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (m1) return new Date(+m1[3], +m1[2]-1, +m1[1], +m1[4], +m1[5], +m1[6]);
  
  // Fallback genérico
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

// ── Paleta (WhatsApp Style) ──────────────────────────────────────────────────
const C = {
  bg:     '#052e16',
  card:   'rgba(255,255,255,0.06)',
  border: 'rgba(255,255,255,0.1)',
  text:   '#f1f5f9',
  muted:  '#bbf7d0',
  dim:    '#86efac',
  green:  '#22c55e',
  red:    '#ef4444',
  blue:   '#3b82f6',
  orange: '#f97316',
  teal:   '#14b8a6',
};

const CHART_CC = [C.green, C.blue, C.orange, C.teal, '#8b5cf6', '#ec4899', '#eab308'];

// ── Estilos ────────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: `linear-gradient(135deg,${C.bg} 0%,#166534 50%,${C.bg} 100%)`, padding: '28px 24px', fontFamily: "'Inter','Segoe UI',sans-serif", color: C.text },
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px 22px', backdropFilter: 'blur(10px)' },
  secTitle: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.green, marginBottom: 16 },
  th: { padding: '9px 12px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.04)' },
  td: { padding: '8px 12px', borderBottom: `1px solid rgba(255,255,255,0.03)`, color: '#e2e8f0', fontSize: '0.82rem' },
  sel: { padding: '7px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.border}`, color: C.text, fontSize: '0.82rem', cursor: 'pointer', outline: 'none' },
  chip: (active, color) => ({ padding: '5px 12px', borderRadius: 20, border: `1px solid ${active ? color : C.border}`, background: active ? `${color}25` : 'rgba(255,255,255,0.04)', color: active ? color : C.muted, fontSize: '0.78rem', fontWeight: active ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s' }),
};

// ── Sub-componentes ─────────────────────────────────────────────────────────────
const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#064e3b', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', color: '#e2e8f0' }}>
      {label && <div style={{ color: C.muted, marginBottom: 4, fontWeight: 700 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || C.green }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('pt-BR') : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${color}`, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: -10, top: -10, fontSize: '3.5rem', opacity: 0.1 }}>{icon}</div>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.73rem', color: C.dim, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children, color = C.green }) {
  return <div style={{ ...S.secTitle, color }}>{children}</div>;
}

// ── Componente principal ────────────────────────────────────────────────────────
const OPENZAP_DESATIVADO = true;

export default function DashboardOpenzap() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [dataDe, setDataDe]         = useState('');
  const [dataAte, setDataAte]       = useState('');
  const [statusSel, setStatusSel]   = useState('all');
  const [agenciaSel, setAgenciaSel] = useState('all');
  const [sentidoSel, setSentidoSel] = useState('all');
  const [operadorSel, setOperadorSel] = useState('all');

  const [datesInitialized, setDatesInitialized] = useState(false);

  useEffect(() => {
    if (OPENZAP_DESATIVADO) { setLoading(false); return; }
    const q = query(collection(db, 'openzap_uploads'), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => {
      setUploads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  const allRows = useMemo(() => uploads.flatMap(u => u.rows || []), [uploads]);

  const enriched = useMemo(() => allRows.map(r => {
    const dt = parseDateTime(r['Data/Hora']);
    const oper = String(r['Usuario'] || r['Tag/Operador'] || '').trim();
    
    return {
      ...r,
      _dt:     dt,
      _hora:   dt ? dt.getHours() : null,
      _diaSem: dt ? dt.getDay() : null,
      _dataStr:dt ? dt.toLocaleDateString('pt-BR') : '',
      _espera: toSeconds(r['Tempo de espera']),
      _duracao:toSeconds(r['DURACAO']),
      _status: r['Status'] ? String(r['Status']).trim().toUpperCase() : '',
      _agencia: r['Agencia'] ? String(r['Agencia']).trim() : 'N/A',
      _sentido: r['Sentido'] ? String(r['Sentido']).trim() : '',
      _operador: oper || 'N/D',
    };
  }), [allRows]);

  const defaultDates = useMemo(() => {
    const dates = enriched.map(r => r._dt).filter(Boolean);
    if (dates.length === 0) return { min: '', max: '' };
    const minD = new Date(Math.min(...dates));
    const maxD = new Date(Math.max(...dates));
    const toInputDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return { min: toInputDate(minD), max: toInputDate(maxD) };
  }, [enriched]);

  useEffect(() => {
    if (enriched.length > 0 && !datesInitialized) {
      if (defaultDates.min && defaultDates.max) {
        setDataDe(defaultDates.min);
        setDataAte(defaultDates.max);
        setDatesInitialized(true);
      }
    }
  }, [enriched, datesInitialized, defaultDates]);

  // Opções únicas para filtros
  const optsAgencias = useMemo(() => [...new Set(enriched.map(r => r._agencia).filter(Boolean))].sort(), [enriched]);
  const optsSentidos = useMemo(() => [...new Set(enriched.map(r => r._sentido).filter(Boolean))].sort(), [enriched]);
  const optsStatus   = useMemo(() => [...new Set(enriched.map(r => r._status).filter(Boolean))].sort(), [enriched]);
  const optsOperador = useMemo(() => [...new Set(enriched.map(r => r._operador).filter(Boolean))].sort(), [enriched]);

  // Filtragem
  const filtered = useMemo(() => {
    let rows = enriched;
    if (dataDe)        { const d = new Date(dataDe);  rows = rows.filter(r => r._dt && r._dt >= d); }
    if (dataAte)       { const d = new Date(dataAte); d.setHours(23,59,59); rows = rows.filter(r => r._dt && r._dt <= d); }
    if (statusSel   !== 'all') rows = rows.filter(r => r._status   === statusSel);
    if (agenciaSel  !== 'all') rows = rows.filter(r => r._agencia  === agenciaSel);
    if (sentidoSel  !== 'all') rows = rows.filter(r => r._sentido  === sentidoSel);
    if (operadorSel !== 'all') rows = rows.filter(r => r._operador === operadorSel);
    return rows;
  }, [enriched, dataDe, dataAte, statusSel, agenciaSel, sentidoSel, operadorSel]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = filtered.length;
    const entrantes = filtered.filter(r => r._sentido === 'Entrante').length;
    const fechados  = filtered.filter(r => r._status === 'FECHADO').length;
    const somaEspera   = filtered.reduce((a, r) => a + r._espera, 0);
    const somaDuracao  = filtered.reduce((a, r) => a + r._duracao, 0);
    const mediaEspera  = total ? Math.round(somaEspera / total) : 0;
    const mediaDuracao = total ? Math.round(somaDuracao / total) : 0;
    const maiorEspera  = filtered.reduce((max, r) => Math.max(max, r._espera), 0);
    const maiorDuracao = filtered.reduce((max, r) => Math.max(max, r._duracao), 0);
    
    return { total, entrantes, fechados, mediaEspera, mediaDuracao, maiorEspera, maiorDuracao };
  }, [filtered]);

  // ── Por Agência ─────────────────────────────────────────────────────────────
  const porAgencia = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const a = r._agencia || 'N/D';
      if (!map[a]) map[a] = { agencia: a, total: 0, entrantes: 0, fechados: 0, somaEspera: 0, somaDuracao: 0 };
      map[a].total++;
      map[a].somaEspera += r._espera;
      map[a].somaDuracao += r._duracao;
      if (r._sentido === 'Entrante') map[a].entrantes++;
      if (r._status === 'FECHADO') map[a].fechados++;
    });
    return Object.values(map)
      .map(a => ({ ...a, mediaEspera: a.total ? Math.round(a.somaEspera/a.total) : 0, mediaDuracao: a.total ? Math.round(a.somaDuracao/a.total) : 0 }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  // ── Volume por dia ─────────────────────────────────────────────────────────
  const volumePorDia = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const d = r._dataStr;
      if (!d) return;
      if (!map[d]) map[d] = { data: d, Entrantes: 0, Saida: 0, _dt: r._dt };
      if (r._sentido === 'Entrante') map[d].Entrantes++;
      else map[d].Saida++;
    });
    return Object.values(map).sort((a, b) => (a._dt||0) - (b._dt||0));
  }, [filtered]);

  const origemData = useMemo(() => {
    const map = {};
    filtered.forEach(r => { map[r._sentido || 'Outros'] = (map[r._sentido || 'Outros'] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  const filtrosAtivos = [statusSel, agenciaSel, sentidoSel, operadorSel].filter(v => v !== 'all').length + (dataDe && dataDe !== defaultDates.min ? 1 : 0) + (dataAte && dataAte !== defaultDates.max ? 1 : 0);

  const limparFiltros = () => {
    if (defaultDates.min && defaultDates.max) {
      setDataDe(defaultDates.min);
      setDataAte(defaultDates.max);
    } else {
      setDataDe('');
      setDataAte('');
    }
    setStatusSel('all'); setAgenciaSel('all'); setSentidoSel('all'); setOperadorSel('all'); 
  };

  if (loading) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>💬</div>
        <div style={{ color: C.green, fontWeight: 700 }}>Carregando dados do WhatsApp...</div>
      </div>
    </div>
  );

  if (OPENZAP_DESATIVADO) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
      <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Dashboard Openzap desativado</h3>
      <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
    </div>
  );

  if (!uploads.length || allRows.length === 0) return (
    <div style={S.page}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <div style={{ width: 48, height: 48, background: `linear-gradient(135deg,${C.green},#16a34a)`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', boxShadow: `0 4px 16px ${C.green}40` }}>💬</div>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>Dashboard Openzap</h1>
          <p style={{ margin: '3px 0 0', fontSize: '0.83rem', color: C.muted }}>Indicadores e análises do WhatsApp</p>
        </div>
      </div>
      <div style={{ ...S.card, textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: '3.5rem', marginBottom: 14 }}>📂</div>
        <div style={{ fontWeight: 700, color: C.muted, marginBottom: 8 }}>Nenhum dado disponível</div>
        <div style={{ color: C.dim, fontSize: '0.85rem' }}>Acesse a aba "Upload Openzap" para subir sua primeira planilha.</div>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      {/* ── CABEÇALHO ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, background: `linear-gradient(135deg,${C.green},#16a34a)`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', boxShadow: `0 4px 16px ${C.green}40` }}>💬</div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>Dashboard Openzap</h1>
            <p style={{ margin: '3px 0 0', fontSize: '0.83rem', color: C.muted }}>
              <strong style={{ color: C.text }}>{filtered.length.toLocaleString('pt-BR')}</strong> interações
              {filtrosAtivos > 0 && <span style={{ color: C.orange }}> · {filtrosAtivos} filtro(s) ativo(s)</span>}
            </p>
          </div>
        </div>
        
        {filtrosAtivos > 0 && (
          <button onClick={limparFiltros} style={{ ...S.chip(true, C.red), padding: '7px 16px' }}>✕ Limpar filtros</button>
        )}
      </div>

      {/* ── BARRA DE FILTROS ──────────────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', padding: '14px 20px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.dim, marginBottom: 3 }}>📅 De</label>
          <input type="date" style={S.sel} value={dataDe} onChange={e => setDataDe(e.target.value)} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.dim, marginBottom: 3 }}>Até</label>
          <input type="date" style={S.sel} value={dataAte} onChange={e => setDataAte(e.target.value)} />
        </div>

        {optsAgencias.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.dim, marginBottom: 3 }}>🏢 Agência</label>
            <select style={S.sel} value={agenciaSel} onChange={e => setAgenciaSel(e.target.value)}>
              <option value="all" style={{ background: '#064e3b', color: '#f1f5f9' }}>Todas</option>
              {optsAgencias.map(v => <option key={v} value={v} style={{ background: '#064e3b', color: '#f1f5f9' }}>{v}</option>)}
            </select>
          </div>
        )}

        {optsSentidos.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.dim, marginBottom: 3 }}>↗️ Sentido</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={S.chip(sentidoSel === 'all', C.green)} onClick={() => setSentidoSel('all')}>Todos</button>
              {optsSentidos.map(v => <button key={v} style={S.chip(sentidoSel === v, C.blue)} onClick={() => setSentidoSel(v)}>{v}</button>)}
            </div>
          </div>
        )}

        {optsStatus.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.dim, marginBottom: 3 }}>🔵 Status</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={S.chip(statusSel === 'all', C.green)} onClick={() => setStatusSel('all')}>Todos</button>
              {optsStatus.map(v => <button key={v} style={S.chip(statusSel === v, v==='FECHADO'?C.green:C.orange)} onClick={() => setStatusSel(v)}>{v}</button>)}
            </div>
          </div>
        )}

        {optsOperador.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.dim, marginBottom: 3 }}>👤 Operador / Usuário</label>
            <select style={S.sel} value={operadorSel} onChange={e => setOperadorSel(e.target.value)}>
              <option value="all" style={{ background: '#064e3b', color: '#f1f5f9' }}>Todos</option>
              {optsOperador.map(v => <option key={v} value={v} style={{ background: '#064e3b', color: '#f1f5f9' }}>{v}</option>)}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* ── LINHA 1: KPI CARDS ────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 14 }}>
          <KpiCard icon="💬" label="Total Interações"     value={kpis.total.toLocaleString('pt-BR')}   color={C.green} />
          <KpiCard icon="📥" label="Total Entrantes"      value={kpis.entrantes.toLocaleString('pt-BR')} color={C.blue} />
          <KpiCard icon="⏱️" label="Méd. Tempo de Espera" value={secsToHMS(kpis.mediaEspera)}          color={C.orange} />
          <KpiCard icon="📞" label="Méd. Tempo Duração"   value={secsToHMS(kpis.mediaDuracao)}         color={C.teal} />
          <KpiCard icon="🚨" label="Maior Tempo Espera"   value={secsToHMS(kpis.maiorEspera)}          color={C.red} />
          <KpiCard icon="📏" label="Maior Duração Total"  value={secsToHMS(kpis.maiorDuracao)}         color={C.purple} />
        </div>

        {/* ── LINHA 2: VOLUME POR DIA E ORIGEM ───────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>
          {/* Volume por dia */}
          <div style={S.card}>
            <SectionTitle>📅 Volume de Interações por Dia</SectionTitle>
            {volumePorDia.length === 0
              ? <div style={{ color: C.dim, textAlign: 'center', padding: '30px 0' }}>Sem dados para o período</div>
              : <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={volumePorDia} barCategoryGap="25%" barGap={2} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="data" tick={{ fill: C.dim, fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                    <YAxis tick={{ fill: C.dim, fontSize: 10 }} />
                    <Tooltip content={<Tip />} />
                    <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.muted }} />
                    <Bar dataKey="Entrantes" fill={C.green} radius={[4,4,0,0]} maxBarSize={18} />
                    <Bar dataKey="Saida"     fill={C.blue}  radius={[4,4,0,0]} maxBarSize={18} />
                  </BarChart>
                </ResponsiveContainer>
            }
          </div>

          {/* Sentidos / Origem */}
          <div style={S.card}>
            <SectionTitle>↗️ Sentido das Chamadas</SectionTitle>
            {origemData.length === 0
              ? <div style={{ color: C.dim, textAlign: 'center', padding: '30px 0' }}>Sem dados de origem</div>
              : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie data={origemData} cx="50%" cy="50%" innerRadius={45} outerRadius={80}
                        dataKey="value" nameKey="name"
                        label={({ percent }) => percent > 0.05 ? `${(percent*100).toFixed(0)}%` : ''}
                        labelLine={false}>
                        {origemData.map((_, i) => <Cell key={i} fill={CHART_CC[i % CHART_CC.length]} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {origemData.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: CHART_CC[i % CHART_CC.length] }} />
                          <span style={{ fontSize: '0.8rem' }}>{item.name}</span>
                        </div>
                        <span style={{ fontWeight: 800, color: CHART_CC[i % CHART_CC.length] }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* ── LINHA 3: DESEMPENHO POR AGÊNCIA ────────────────────────────── */}
        <div style={S.card}>
          <SectionTitle color={C.green}>🏢 Desempenho por Agência</SectionTitle>
          <div style={{ overflowX: 'auto', maxHeight: 300 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={S.th}>Agência</th>
                  <th style={{...S.th, textAlign:'right'}}>Total</th>
                  <th style={{...S.th, textAlign:'right'}}>Entrantes</th>
                  <th style={{...S.th, textAlign:'right'}}>Fechados</th>
                  <th style={{...S.th, textAlign:'right'}}>Média Espera</th>
                  <th style={{...S.th, textAlign:'right'}}>Média Duração</th>
                </tr>
              </thead>
              <tbody>
                {porAgencia.map(a => (
                  <tr key={a.agencia}>
                    <td style={S.td}>{a.agencia}</td>
                    <td style={{...S.td, textAlign:'right', fontWeight: 700}}>{a.total}</td>
                    <td style={{...S.td, textAlign:'right', color: C.green}}>{a.entrantes}</td>
                    <td style={{...S.td, textAlign:'right'}}>{a.fechados}</td>
                    <td style={{...S.td, textAlign:'right', color: C.orange}}>{secsToHMS(a.mediaEspera)}</td>
                    <td style={{...S.td, textAlign:'right', color: C.teal}}>{secsToHMS(a.mediaDuracao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
